import { ProductSummary } from "../../../ProductSummary";
import "./styles.css";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../../../services/axios";
import { DeleteModalCreateProduct, SuccessModalCreateProduct } from "./Modal/Index";
import { useEditOnboarding } from "../../../../hooks/useEditOnboarding";
import { useFilter } from "../../../../hooks/useFilter";
import { OptionV2 } from "../../../FormControlsV2";
import { FooterEditOnboarding } from "../../../EditOnboarding/Footer";
import { useTranslation } from "react-i18next";
import { getLocalizedApiName, LocalizedApiName } from "../../../../utils/i18n/getLocalizedApiName";
import {
  createEmptyProductFormValue,
  ProductFormErrorsV2,
  ProductFormV2,
  ProductFormValueV2,
} from "../../../FormsV2";

type SupplementClassification = {
  id: number;
  name: LocalizedApiName;
  sub_classification: Array<{ id: number; name: LocalizedApiName }>;
};

function getOptionLabel(value: any) {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.label || value.name || value.value || "");
}

function getOptionValue(value: any) {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.value || value.id || value.label || value.name || "");
}

function createProductFormFromDefault(defaultValue: any, classifications: OptionV2[]) {
  const defaultClassificationLabel = getOptionLabel(defaultValue?.classification);
  const defaultClassificationValue = getOptionValue(defaultValue?.classification_id || defaultValue?.classification);
  const classification =
    classifications.find((option) => String(option.value) === defaultClassificationValue) ||
    classifications.find((option) => option.label === defaultClassificationLabel) ||
    null;
  const subClassificationOptions = Array.isArray(classification?.info) ? (classification?.info as OptionV2[]) : [];
  const defaultSubClassificationLabel = getOptionLabel(defaultValue?.sub_classification);
  const defaultSubClassificationValue = getOptionValue(
    defaultValue?.sub_classification_id || defaultValue?.sub_classification
  );
  const subClassification =
    subClassificationOptions.find((option) => String(option.value) === defaultSubClassificationValue) ||
    subClassificationOptions.find((option) => option.label === defaultSubClassificationLabel) ||
    null;

  return {
    id: defaultValue?.id,
    creep_feeding: defaultValue?.creep_feeding ?? false,
    trade_name: defaultValue?.trade_name || "",
    manufacturer: defaultValue?.manufacturer || "",
    classification,
    sub_classification: subClassification,
    packaging_type: defaultValue?.packaging_type || "BULK",
    bag_weight_kg: defaultValue?.bag_weight_kg == null ? null : Number(defaultValue.bag_weight_kg),
    expected_consumption: Number(defaultValue?.expected_consumption || 0),
    supply_frequency: Number(defaultValue?.supply_frequency || 0),
  } as ProductFormValueV2;
}

export function CreateProducts() {
  const { t, i18n } = useTranslation();
  const { setOptionForm, defaultValue } = useEditOnboarding();
  const { setSideBarOptionSelected } = useFilter();
  const [productValue, setProductValue] = useState<ProductFormValueV2>(() => createEmptyProductFormValue());
  const initializedProduct = useRef<any>(undefined);
  const [errors, setErrors] = useState<ProductFormErrorsV2>({});
  const [classificationData, setClassificationData] = useState<SupplementClassification[]>([]);
  const [modalDeleteVisible, setModalDeleteVisible] = useState(false);
  const [successCreator, setSuccessCreator] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const classifications = useMemo<OptionV2[]>(
    () => classificationData.map((classification) => ({
      label: getLocalizedApiName(classification.name, i18n.language),
      value: classification.id,
      info: classification.sub_classification.map((subClassification) => ({
        label: getLocalizedApiName(subClassification.name, i18n.language),
        value: subClassification.id,
      })),
    })),
    [classificationData, i18n.language]
  );

  const subClassificationLabel = productValue.classification?.value === 1
    ? t("formsV2.saltType")
    : t("formsV2.expectedConsumptionClassification");

  const formIsComplete = !!(
    productValue.trade_name.trim() &&
    productValue.manufacturer.trim() &&
    productValue.classification &&
    productValue.sub_classification &&
    (productValue.packaging_type === "BULK" || Number(productValue.bag_weight_kg) > 0) &&
    productValue.expected_consumption > 0 &&
    productValue.supply_frequency > 0
  );

  function validate(value = productValue) {
    const validation: ProductFormErrorsV2 = {};

    if (!value.trade_name.trim()) validation.trade_name = t("internalValidation.productNameRequired");
    if (!value.manufacturer.trim()) validation.manufacturer = t("internalValidation.manufacturerRequired");
    if (!value.classification) validation.classification = t("internalValidation.required");
    if (!value.sub_classification) validation.sub_classification = t("internalValidation.required");
    if (!value.packaging_type) validation.packaging_type = t("internalValidation.required");
    if (value.packaging_type === "BAGGED" && Number(value.bag_weight_kg) <= 0) {
      validation.bag_weight_kg = t("productPackaging.bagWeightRequired");
    }
    if (value.expected_consumption <= 0) validation.expected_consumption = t("internalValidation.expectedConsumption");
    if (value.supply_frequency <= 0) validation.supply_frequency = t("internalValidation.supplyFrequency");

    setErrors(validation);
    return Object.keys(validation).length === 0;
  }

  function handleChange(nextValue: ProductFormValueV2) {
    setProductValue(nextValue);
    if (Object.keys(errors).length) validate(nextValue);
  }

  function submitProduct() {
    if (submitting || !validate()) return;

    const requestBody = {
      creep_feeding: productValue.creep_feeding,
      manufacturer: productValue.manufacturer,
      trade_name: productValue.trade_name,
      sub_classification_id: Number(productValue.sub_classification?.value),
      expected_consumption: productValue.expected_consumption,
      supply_frequency: productValue.supply_frequency,
      packaging_type: productValue.packaging_type,
      bag_weight_kg: productValue.packaging_type === "BAGGED" ? productValue.bag_weight_kg : null,
    };

    setSubmitting(true);
    setSubmitError("");
    const request = defaultValue
      ? axios.patch("/products/supplement", { id: productValue.id, ...requestBody })
      : axios.post("/products/supplement", requestBody);

    request
      .then(() => setSuccessCreator(true))
      .catch((error) => setSubmitError(
        error.response?.data?.message || error.response?.data?.msg || t("managementUi.somethingWrong")
      ))
      .finally(() => setSubmitting(false));
  }

  function deleteProduct(id: number) {
    axios.delete(`/products/${id}`).then(() => {
      setOptionForm(undefined);
      setModalDeleteVisible(false);
    });
  }

  useEffect(() => {
    axios.get("/products/supplement/classification").then(({ data }) => {
      setClassificationData(data);
    });
  }, []);

  useEffect(() => {
    if (!classifications.length) return;

    if (defaultValue && initializedProduct.current !== defaultValue) {
      initializedProduct.current = defaultValue;
      setProductValue(createProductFormFromDefault(defaultValue, classifications));
      return;
    }

    initializedProduct.current = defaultValue;
    setProductValue((current) => {
      if (!current.classification) return current;
      const classification = classifications.find(
        (option) => String(option.value) === String(current.classification?.value)
      );
      const classificationInfo = classification?.info;
      const subClassifications = Array.isArray(classificationInfo) ? classificationInfo as OptionV2[] : [];
      const subClassification = subClassifications.find(
        (option) => String(option.value) === String(current.sub_classification?.value)
      ) || null;

      return { ...current, classification: classification || null, sub_classification: subClassification };
    });
  }, [classifications, defaultValue]);

  const summarySlot = (
    <aside className="create-product-v2__summary">
      <ProductSummary product={productValue} />
    </aside>
  );

  return (
    <>
      <main className="body__formEditOnboarding create-product-v2">
        <ProductFormV2
          value={productValue}
          errors={errors}
          classifications={classifications}
          subClassificationLabel={subClassificationLabel}
          sideSlot={summarySlot}
          disabled={submitting}
          onChange={handleChange}
        />
        {submitError && <p role="alert" className="ov2-field__error">{submitError}</p>}
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={submitProduct}
        deleteFunction={defaultValue?.can_delete ? () => setModalDeleteVisible(true) : undefined}
        isNew={!defaultValue}
        disabledSubmit={!formIsComplete || submitting}
      />

      <SuccessModalCreateProduct
        visible={successCreator}
        onClose={() => {
          setOptionForm(undefined);
          setSuccessCreator(false);
        }}
        backScreen={() => {
          setSideBarOptionSelected("MAPA");
          setOptionForm(undefined);
          setSuccessCreator(false);
        }}
        newProduct={() => {
          setSuccessCreator(false);
          setOptionForm(undefined);
          setTimeout(() => setOptionForm("create-products"), 1);
        }}
      />

      <DeleteModalCreateProduct
        visible={modalDeleteVisible}
        onClose={() => setModalDeleteVisible(false)}
        onConfirm={() => defaultValue?.id && deleteProduct(defaultValue.id)}
      />
    </>
  );
}
